/**
 * Journey page: Extend & Automate (J2 + J8 + J9)
 *
 * Peak state: agent (Rex) hit capability wall — needs LinkedIn OAuth.
 * OAuthPromptCard floats on canvas. WorkflowFrame shows scheduled digest.
 * - J2: Agent team sidebar, Add Agent CTA
 * - J8: OAuthPromptCard surfaced mid-task, agent blocked
 * - J9: WorkflowFrame with schedule badge, SchedulePanel in right panel
 */

import { useState, useRef, useEffect } from "react";
import { useCanvasTransform } from "@/workshop/components/canvas/canvas-viewport";
import { Icons } from "@/components/ui/icons";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";
import { AgentSubtitleBar } from "@/workshop/components/canvas/agent-subtitle-bar";
import { OAuthPromptCard } from "@/workshop/components/canvas/oauth-prompt-card";
import { ConnectionFrame } from "@/workshop/components/shapes/connection-frame/connection-frame";
import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import { MOCK_WORKFLOW_STEPS } from "@/workshop/data/mock-journeys";

// ─── Types ────────────────────────────────────────────────────────────────────

type Pos = { x: number; y: number };

// ─── Initial frame positions ──────────────────────────────────────────────────

const INITIAL_POS = {
  connection: { x: 320, y: 80 } as Pos,
  workflow: { x: 620, y: 90 } as Pos,
};

// ─── Workflow steps for the scheduled digest ──────────────────────────────────

const DIGEST_STEPS = MOCK_WORKFLOW_STEPS.map((step) => ({
  ...step,
  status: "completed" as const,
}));

// ─── Canvas content ───────────────────────────────────────────────────────────

function CanvasContent() {
  // --- positions & drag ---
  const [pos, setPos] = useState(INITIAL_POS);
  const drag = useRef<{
    key: string;
    sx: number;
    sy: number;
    px: number;
    py: number;
  } | null>(null);
  const { zoom, isPanMode } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const dotSpacing = `${24 / zoom}px`;
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!drag.current) return;
      const { key, sx, sy, px, py } = drag.current;
      setPos((p) => ({
        ...p,
        [key]: {
          x: px + (e.clientX - sx) / zoomRef.current,
          y: py + (e.clientY - sy) / zoomRef.current,
        },
      }));
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  function grab(key: string, e: React.MouseEvent) {
    if (isPanMode) return;
    e.preventDefault();
    drag.current = {
      key,
      sx: e.clientX,
      sy: e.clientY,
      px: pos[key as keyof typeof pos].x,
      py: pos[key as keyof typeof pos].y,
    };
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-black"
      data-testid="extend-automate-canvas"
      style={{
        backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
        backgroundSize: `${dotSpacing} ${dotSpacing}`,
      }}
    >
      {/* Blocked indicator — moves with ConnectionFrame */}
      <div
        className="absolute z-10"
        style={{ left: pos.connection.x, top: pos.connection.y - 36 }}
      >
        <AgentSubtitleBar
          agentName="Rex"
          actionText="blocked · awaiting LinkedIn auth"
          dotColor="bg-yellow-500"
          data-testid="connection-frame-blocked-indicator"
        />
      </div>

      {/* Frame: ConnectionFrame — LinkedIn awaiting auth */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none ring-1 ring-yellow-500/40 rounded-xl"
        style={{ left: pos.connection.x, top: pos.connection.y, width: 240 }}
        onMouseDown={(e) => grab("connection", e)}
        data-testid="canvas-frame-connection"
      >
        <ConnectionFrame
          title="Connected Apps"
          agentName="Rex"
          apps={[
            { id: "linkedin", connected: false },
            { id: "gmail", connected: true },
            { id: "notion", connected: true },
            { id: "github", connected: false },
            { id: "slack", connected: true },
          ]}
        />
      </div>

      {/* Frame: WorkflowFrame — Weekly Digest, idle/done */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.workflow.x, top: pos.workflow.y, width: 280 }}
        onMouseDown={(e) => grab("workflow", e)}
        data-testid="canvas-frame-workflow-wrapper"
      >
        <WorkflowFrame
          title="Weekly Digest"
          flowSteps={DIGEST_STEPS}
          status="idle"
          agentName="Aria"
        />
      </div>

      {/* Schedule badge chip — below WorkflowFrame */}
      <div
        className="absolute"
        style={{ left: pos.workflow.x, top: pos.workflow.y + 200 }}
        data-testid="workflow-schedule-badge-wrapper"
      >
        <div
          className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-0.5 text-[10px] font-medium text-blue-400"
          data-testid="workflow-schedule-badge"
        >
          <Icons.Clock className="h-2.5 w-2.5" />
          Runs every Monday 9am
        </div>
      </div>

      {/* OAuthPromptCard — floating, Rex blocked on LinkedIn, not draggable */}
      <div
        className="absolute z-20"
        style={{ left: 320, top: 380 }}
        data-testid="canvas-oauth-prompt-wrapper"
      >
        <OAuthPromptCard
          appName="LinkedIn"
          appInitial="Li"
          agentName="Rex"
          reason="Rex needs LinkedIn access to post. Connect your account to continue."
        />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ExtendAutomatePage() {
  return (
    <WorkshopLayoutProvider defaultSidebarOpen defaultRightPanelOpen>
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="grayNoise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div
        className="h-screen w-screen overflow-hidden"
        data-testid="extend-automate-page"
      >
        <WorkshopMasterLayout>
          <CanvasContent />
        </WorkshopMasterLayout>
      </div>
    </WorkshopLayoutProvider>
  );
}
